
import React, { useState } from 'react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { toast } from "@/hooks/use-toast";

const RetirementCalculator: React.FC = () => {
  const [currentAge, setCurrentAge] = useState<number>(30);
  const [retirementAge, setRetirementAge] = useState<number>(65);
  const [lifeExpectancy, setLifeExpectancy] = useState<number>(85);
  const [currentSavings, setCurrentSavings] = useState<number>(100000);
  const [monthlySavings, setMonthlySavings] = useState<number>(1000);
  const [annualReturn, setAnnualReturn] = useState<number>(7);
  const [inflationRate, setInflationRate] = useState<number>(2.5);
  const [desiredAnnualIncome, setDesiredAnnualIncome] = useState<number>(60000);
  
  const [retirementSavings, setRetirementSavings] = useState<number>(0);
  const [retirementIncome, setRetirementIncome] = useState<number>(0);
  const [incomeShortfall, setIncomeShortfall] = useState<number>(0);
  const [additionalSavingsNeeded, setAdditionalSavingsNeeded] = useState<number>(0);

  const calculateRetirement = () => {
    try {
      // Validation
      if (currentAge < 0 || retirementAge <= currentAge || lifeExpectancy <= retirementAge ||
          currentSavings < 0 || monthlySavings < 0 || annualReturn < 0 || 
          inflationRate < 0 || desiredAnnualIncome < 0) {
        toast({
          title: "Invalid input",
          description: "Please enter valid values for all fields",
          variant: "destructive"
        });
        return;
      }

      // Years until retirement and years in retirement
      const yearsToRetirement = retirementAge - currentAge;
      const yearsInRetirement = lifeExpectancy - retirementAge;

      // Convert annual rates to decimal
      const annualReturnRate = annualReturn / 100;
      const inflationRateDecimal = inflationRate / 100;

      // Calculate real rate of return (adjusted for inflation)
      const realRateOfReturn = (1 + annualReturnRate) / (1 + inflationRateDecimal) - 1;

      // Calculate future value of current savings at retirement
      const futureSavings = currentSavings * Math.pow(1 + annualReturnRate, yearsToRetirement);

      // Calculate future value of monthly contributions at retirement
      const monthlyReturnRate = Math.pow(1 + annualReturnRate, 1/12) - 1;
      const monthlyContributionsFV = monthlySavings * 
        ((Math.pow(1 + monthlyReturnRate, yearsToRetirement * 12) - 1) / monthlyReturnRate) * 
        (1 + monthlyReturnRate);

      // Total retirement savings
      const totalRetirementSavings = futureSavings + monthlyContributionsFV;
      
      // Adjust desired annual income for inflation at retirement
      const inflatedDesiredIncome = desiredAnnualIncome * Math.pow(1 + inflationRateDecimal, yearsToRetirement);
      
      // Calculate sustainable annual withdrawal (4% rule as a simple approximation)
      const sustainableAnnualWithdrawal = totalRetirementSavings * 0.04;
      
      // Calculate income shortfall
      const annualShortfall = Math.max(0, inflatedDesiredIncome - sustainableAnnualWithdrawal);
      
      // Calculate additional savings needed to cover the shortfall
      const additionalCapitalNeeded = annualShortfall / 0.04;

      setRetirementSavings(totalRetirementSavings);
      setRetirementIncome(sustainableAnnualWithdrawal);
      setIncomeShortfall(annualShortfall);
      setAdditionalSavingsNeeded(additionalCapitalNeeded);

      toast({
        title: "Calculation complete",
        description: "Your retirement projection has been calculated"
      });
    } catch (error) {
      toast({
        title: "Calculation error",
        description: "An error occurred during calculation",
        variant: "destructive"
      });
    }
  };

  const formatCurrency = (value: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  return (
    <div className="space-y-6">
      <div className="grid gap-6 md:grid-cols-2">
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="currentAge">Current Age</Label>
              <Input
                id="currentAge"
                type="number"
                value={currentAge}
                onChange={(e) => setCurrentAge(Number(e.target.value))}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="retirementAge">Retirement Age</Label>
              <Input
                id="retirementAge"
                type="number"
                value={retirementAge}
                onChange={(e) => setRetirementAge(Number(e.target.value))}
              />
            </div>
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="lifeExpectancy">Life Expectancy</Label>
            <div className="flex items-center gap-4">
              <Input
                id="lifeExpectancy"
                type="number"
                value={lifeExpectancy}
                onChange={(e) => setLifeExpectancy(Number(e.target.value))}
              />
              <span className="text-sm text-muted-foreground w-6">{lifeExpectancy}</span>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="currentSavings">Current Retirement Savings ($)</Label>
            <Input
              id="currentSavings"
              type="number"
              value={currentSavings}
              onChange={(e) => setCurrentSavings(Number(e.target.value))}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="monthlySavings">Monthly Contributions ($)</Label>
            <Input
              id="monthlySavings"
              type="number"
              value={monthlySavings}
              onChange={(e) => setMonthlySavings(Number(e.target.value))}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="annualReturn">Expected Annual Return (%)</Label>
            <div className="flex items-center gap-4">
              <Slider
                id="annualReturn"
                min={0}
                max={12}
                step={0.1}
                value={[annualReturn]}
                onValueChange={(values) => setAnnualReturn(values[0])}
                className="flex-1"
              />
              <span className="text-sm text-muted-foreground w-10">{annualReturn}%</span>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="inflationRate">Expected Inflation Rate (%)</Label>
            <div className="flex items-center gap-4">
              <Slider
                id="inflationRate"
                min={0}
                max={8}
                step={0.1}
                value={[inflationRate]}
                onValueChange={(values) => setInflationRate(values[0])}
                className="flex-1"
              />
              <span className="text-sm text-muted-foreground w-10">{inflationRate}%</span>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="desiredAnnualIncome">Desired Annual Retirement Income ($)</Label>
            <Input
              id="desiredAnnualIncome"
              type="number"
              value={desiredAnnualIncome}
              onChange={(e) => setDesiredAnnualIncome(Number(e.target.value))}
            />
          </div>

          <Button onClick={calculateRetirement} className="mt-4 w-full">Calculate Retirement Plan</Button>
        </div>

        <div>
          <Card className="border-2 border-primary/20">
            <CardContent className="p-6">
              <h3 className="text-lg font-semibold mb-4">Retirement Projection</h3>
              
              <div className="space-y-6">
                <div>
                  <div className="text-sm text-muted-foreground">Years until retirement</div>
                  <div className="text-lg font-medium">{retirementAge - currentAge} years</div>
                </div>
                
                <div>
                  <div className="text-sm text-muted-foreground">Projected Retirement Savings</div>
                  <div className="text-xl font-bold text-primary">
                    {retirementSavings ? formatCurrency(retirementSavings) : '-'}
                  </div>
                </div>
                
                <div>
                  <div className="text-sm text-muted-foreground">Estimated Annual Retirement Income</div>
                  <div className="text-lg font-medium">
                    {retirementIncome ? formatCurrency(retirementIncome) : '-'}
                  </div>
                </div>
                
                {incomeShortfall > 0 && (
                  <>
                    <div className="pt-4 border-t">
                      <div className="text-sm text-muted-foreground">Annual Income Shortfall</div>
                      <div className="text-lg font-medium text-destructive">
                        {formatCurrency(incomeShortfall)}
                      </div>
                    </div>
                    
                    <div>
                      <div className="text-sm text-muted-foreground">Additional Savings Needed</div>
                      <div className="text-lg font-medium text-destructive">
                        {formatCurrency(additionalSavingsNeeded)}
                      </div>
                    </div>
                  </>
                )}
                
                {incomeShortfall === 0 && retirementSavings > 0 && (
                  <div className="pt-4 border-t">
                    <div className="text-lg font-medium text-green-600">
                      You're on track to meet your retirement goals!
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
          
          <div className="mt-4 px-1">
            <p className="text-sm text-muted-foreground">
              This calculator uses the 4% rule as a simplified withdrawal strategy. Actual retirement
              needs may vary based on lifestyle, healthcare costs, and other factors.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RetirementCalculator;
